<template>

    <LoadingDiv v-if="!isReady" />

    <div class="finance-accounts-workbench sba-open-workbench" v-else>

        <div class="accounts-review-head">
            <div class="sba-open-title">
                <label class="active teal-text">Finance Accounts</label>
                <h5>Provider Account Updates</h5>
                <p>Review providers with pending finance account details and update bank information carefully.</p>
            </div>

            <span class="accounts-count sba-open-count">
                {{ providers.length }}
                <small>{{ providers.length === 1 ? "provider" : "providers" }}</small>
            </span>
        </div>

        <div class="accounts-list-card">
            <div class="account-row-card sba-open-row"
                :class="{ 'has-error': error && editProvider === provider, 'is-open': editProvider === provider }" v-for="provider in providers"
                :key="provider.id">
                <div class="account-row-main">
                    <div class="account-provider">
                        <span class="account-avatar">
                            <i class="material-icons">account_balance</i>
                        </span>
                        <div>
                            <label class="active teal-text">Provider</label>
                            <h6>{{ capWords(provider.name) }}</h6>
                            <p>Pending bank account details require finance confirmation.</p>
                        </div>
                    </div>

                    <button
                        type="button"
                        class="account-row-action"
                        :class="{ 'is-close': editProvider === provider }"
                        @click.prevent="handleEdit(provider)"
                        :title="editProvider === provider ? 'Close' : 'Edit account'"
                    >
                        <i class="material-icons">{{ editProvider === provider ? "close" : "edit" }}</i>
                    </button>
                </div>

                <div class="account-edit-panel" v-if="editProvider === provider">
                    <div class="row mb-zero">
                    <div class="col s12 m7">
                        <div class="account-input-card">
                            <label class="active teal-text" for="bank">Bank</label>
                            <input id="bank" type="text" v-model="bank" />
                        </div>
                    </div>
                    <div class="col s12 m5">
                        <div class="account-input-card">
                            <label class="active teal-text" for="account">Account Number</label>
                            <input id="account" type="number" v-model="account" />
                        </div>
                    </div>
                    <div class="col s12">
                        <button class="account-submit right" :class="{
                            disabled: isSubmitting || !bank || !account,
                        }" @click.prevent="handleSubmit()">
                            Update
                            <i class="material-icons right" title="go back">check_circle</i>
                        </button>
                    </div>
                    </div>

                </div>

            </div>
        </div>

        <div class="accounts-empty-state" v-if="!providers.length">
            <i class="material-icons">verified</i>
            <h6>No provider account updates pending</h6>
            <p>Finance account details that need attention will appear here.</p>
        </div>

    </div>

</template>


<script>
import { computed, onMounted, ref, watch } from "vue";
import getItems from "@/composables/getItems";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import {
    capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";


export default {
    name: "FinanceAccountsUpdate",
    components: { LoadingDiv },
    props: ["resetKey"],
    setup(props) {
        const response = ref(null);
        const isReady = ref(false);
        const isSubmitting = ref(false);
        const error = ref(null);
        const bank = ref(null);
        const account = ref(null);
        const editProvider = ref(null);

        // Initiate
        onMounted(() => {
            // load
            loadProvider();
        });

        // loader
        const loadProvider = () => {
            // start   
            isReady.value = false;
            // get provider
            (async () => {
                const { res, err } = await getItems("cluster/finance/pending/account");

                if (res.value) {
                    response.value = res.value.data;
                } else {
                    error.value = err.value;
                }

                // page is ready
                isReady.value = true;
            })();
        }

        //from functions
        const capWords = (val) => {
            return capWordsFx(val);
        };

        // handle clear
        const handleClear = () => {
            editProvider.value = null;
            bank.value = null;
            account.value = null;
            error.value = null;
            isSubmitting.value = false;
        };

        watch(
            () => props.resetKey,
            () => {
                handleClear();
            }
        );

        // handle emitted update
        const handleEdit = (val) => {
            // start
            let isOk = true;

            // load only empties
            if (editProvider.value === val) {
                isOk = false;
            }

            // clear all
            handleClear();

            // update
            if (isOk) {
                editProvider.value = val;
            }
        };

        // handle submit
        const handleSubmit = async () => {
            if (bank.value && account.value) {

                // start
                isSubmitting.value = true;
                error.value = false;


                // prepare for take off
                const newVal = {
                    bank: bank.value,
                    account: account.value,
                };

                // post item
                const { res, err } = await putItem(
                    "provider",
                    editProvider.value.id,
                    newVal
                );

                if (res.value) {
                    //update all
                    handleClear();
                    // reload
                    loadProvider();
                } else {
                    error.value = err.value;
                }

                // end here
                isSubmitting.value = false;
            }

        }


        // output
        const providers = computed(() => {
            if (!response.value) {
                return [];
            } else {
                let providersOnly = [];
                response.value.forEach((x) => {
                    if (x.provider && !providersOnly.some((provider) => provider.id === x.provider.id)) {
                        providersOnly.push(x.provider);
                    }
                });
                return providersOnly;
            }
        });

        return {
            capWords,
            handleEdit,
            handleSubmit,
            editProvider,
            bank,
            account,
            isReady,
            isSubmitting,
            error,
            providers,
        };
    },
};
</script>

<style scoped>
.finance-accounts-workbench {
    color: #263238;
}

.accounts-list-card,
.account-row-card {
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    box-shadow: none;
}

.accounts-review-head {
    align-items: end;
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--ui-border);
    border-radius: 0;
    box-shadow: none;
    display: grid;
    gap: 2rem;
    grid-template-columns: minmax(0, 1fr) auto;
    margin: 0 0 1.5rem;
    padding: 0 0 1.25rem;
}

.accounts-review-head h5 {
    color: var(--ui-foreground);
    font-family: var(--ui-font-sans);
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.35;
    margin: 0;
}

.accounts-review-head p {
    color: var(--ui-muted-foreground);
    font-family: var(--ui-font-sans);
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0.35rem 0 0;
}

.accounts-count {
    align-items: baseline;
    background: transparent;
    border: 0;
    border-radius: 0;
    color: var(--ui-foreground);
    display: inline-flex;
    flex-direction: row;
    font-family: var(--ui-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    gap: 0.3rem;
    line-height: 1.4;
    min-width: 0;
    padding: 0 0 0.15rem;
}

.accounts-count small {
    color: var(--ui-muted-foreground);
    font-size: inherit;
    font-weight: 500;
}

.accounts-list-card {
    background: transparent;
    border: none;
    display: grid;
    gap: 3px;
    margin-top: 0;
}

.account-row-card {
    box-shadow: 0 10px 22px -20px rgba(38, 50, 56, 0.55);
    padding: 22px 22px;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.accounts-list-card > .account-row-card:first-child {
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
}

.account-row-card:hover {
    border-color: #d8e1e5;
    box-shadow: 0 14px 28px -22px rgba(38, 50, 56, 0.7);
    transform: translateY(-1px);
}

.account-row-card.is-open {
    border-color: #80cbc4;
}

.account-row-card.has-error {
    background: #ffebee;
    border-color: #ffcdd2;
}

.account-row-main {
    align-items: center;
    display: flex;
    gap: 18px;
    justify-content: space-between;
}

.account-provider {
    align-items: center;
    display: flex;
    gap: 16px;
    min-width: 0;
}

.account-avatar {
    align-items: center;
    background: #e0f2f1;
    border-radius: 50%;
    color: #00838f;
    display: inline-flex;
    flex: 0 0 48px;
    height: 48px;
    justify-content: center;
    width: 48px;
}

.account-avatar i {
    font-size: 24px;
}

.account-provider h6 {
    color: #263238;
    font-size: 1.05rem;
    font-weight: 700;
    margin: 2px 0 0;
}

.account-provider p {
    color: #607d8b;
    line-height: 1.4;
    margin: 5px 0 0;
}

.account-row-action {
    align-items: center;
    background: #fafafa;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    color: #607d8b;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 38px;
    height: 38px;
    justify-content: center;
    padding: 0;
    width: 38px;
}

.account-row-action.is-close {
    background: #263238;
    border-color: #263238;
    color: #ffffff;
}

.account-edit-panel {
    background: #fafafa;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    margin-top: 18px;
    padding: 16px;
}

.account-input-card {
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    padding: 12px 14px 4px;
}

.account-input-card input {
    border-bottom: 0 !important;
    box-shadow: none !important;
    color: #263238;
    font-weight: 700;
    margin-bottom: 0;
}

.account-submit {
    background: #00838f;
    border: none;
    border-radius: 6px;
    color: #ffffff;
    cursor: pointer;
    font-weight: 700;
    margin-top: 14px;
    min-height: 38px;
    padding: 0 16px;
}

.account-submit.disabled {
    background: #cfd8dc !important;
    color: #607d8b !important;
    cursor: default;
}

.accounts-empty-state {
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    color: #607d8b;
    margin-top: 3px;
    padding: 32px 22px;
    text-align: center;
}

.accounts-empty-state i {
    color: #00838f;
    font-size: 34px;
}

.accounts-empty-state h6 {
    color: #263238;
    font-weight: 700;
    margin: 10px 0 0;
}

.accounts-empty-state p {
    margin: 6px 0 0;
}

.pointer {
    cursor: default;
}

/* base class */
.tab-custom-small {
    display: inline-block;
    cursor: pointer;
    margin: 0 0 -1px;
    padding: 15px 25px;
    font-weight: 600;
    text-align: center;
    color: #bbb;
    border: 1px solid transparent;
}

.tab-custom-small:hover {
    color: #000000 !important;
}

/* Removed
  color: #000000;
  border: 1px solid #ddd;
  background-color: #d1f3df; */
.tab-custom-active-small {
    display: inline-block;
    cursor: pointer;
    margin: 0 0 -1px;
    padding: 15px 25px;
    font-weight: 600;
    text-align: center;
    border-top: 2px solid teal;
}

/* display */
.tab-custom-section {
    padding: 20px 0 0;
    border-top: 1px solid #ddd;
}

@media only screen and (max-width: 600px) {
    .accounts-review-head {
        align-items: flex-start;
        gap: 0.75rem;
        grid-template-columns: minmax(0, 1fr);
        padding: 0 0 1.25rem;
    }

    .accounts-review-head h5 {
        font-size: 1.25rem;
        line-height: 1.35;
    }

    .accounts-list-card {
        border-radius: 6px;
        overflow: hidden;
    }

    .account-row-card {
        padding: 18px;
    }

    .account-row-main,
    .account-provider {
        align-items: flex-start;
    }

    .account-provider {
        flex-direction: column;
        gap: 12px;
    }

    .account-row-main {
        flex-direction: column;
    }

    .account-row-action,
    .account-submit {
        width: 100%;
    }
}
</style>
